import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../../lib/utils';
import { buttonVariants } from './variants';
import type { ButtonProps } from './types';

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'black',
      size = 'default',
      asChild = false,
      disabled,
      focusableWhenDisabled = false,
      onClick,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    // Native `disabled` drops a focused button's focus to <body> without a blur
    // event, so keyboard users lose their place, e.g. while a request runs.
    const isFocusablyDisabled = Boolean(disabled && focusableWhenDisabled);

    return (
      <Comp
        ref={ref}
        data-slot='button'
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
        disabled={isFocusablyDisabled ? undefined : disabled}
        aria-disabled={isFocusablyDisabled || props['aria-disabled']}
        // Styles key off this, not aria-disabled, which callers also set alone.
        data-focusable-disabled={isFocusablyDisabled || undefined}
        onClick={
          isFocusablyDisabled
            ? (event: React.MouseEvent<HTMLButtonElement>) => {
                // Like native `disabled`: no submit, no navigation, no bubbling.
                event.preventDefault();
                event.stopPropagation();
              }
            : onClick
        }
      />
    );
  },
);

Button.displayName = 'Button';
